import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { IPendingResourceAuthorization, IResourceAuthorization } from '../../models/resource_authorization'
import { logout, refreshSession } from '../actions/constants'
import dateUtils from '../../utils/date_utils'

export type RelationToggleKey = 'customerPanel' | 'zestMagazine' | 'cooperationMembership'
export interface RelationOverrides {
  customerPanel?: boolean
  zestMagazine?: boolean
  cooperationMembership?: boolean
  expiresAt?: number
}

export interface IResourceAuthorizationState {
  resourceAuthorization?: IResourceAuthorization
  resourceAuthorizations: IResourceAuthorization[]
  isLoading: boolean
  localOverridesByRelation: Record<string, RelationOverrides>
  pendingResourceAuthorizations?: IPendingResourceAuthorization[]
  managedResourceAuthorizations?: IResourceAuthorization[]
}

const initialState: IResourceAuthorizationState = {
  resourceAuthorization: undefined,
  resourceAuthorizations: [],
  isLoading: false,
  localOverridesByRelation: {},
  pendingResourceAuthorizations: [],
  managedResourceAuthorizations: []
}

export const resourceAuthorizationSlice = createSlice({
  name: 'resourceAuthorization',
  initialState,
  reducers: {
    setResourceAuthorization: (state, action: PayloadAction<IResourceAuthorization | undefined>) => {
      state.resourceAuthorization = action.payload
    },
    setResourceAuthorizationIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setResourceAuthorizations: (state, action: PayloadAction<IResourceAuthorization[]>) => {
      state.resourceAuthorizations = action.payload
    },
    setManagedResourceAuthorizations: (state, action: PayloadAction<{ pendingResourceAuthorizations: IPendingResourceAuthorization[]; managedResourceAuthorizations: IResourceAuthorization[] }>) => {
      state.pendingResourceAuthorizations = action.payload.pendingResourceAuthorizations
      state.managedResourceAuthorizations = action.payload.managedResourceAuthorizations
    },
    setLocalRelationToggle: (
      state,
      action: PayloadAction<{
        relationNumber: string
        key: RelationToggleKey
        value: boolean
        expiresAt?: number
      }>
    ) => {
      const { relationNumber, key, value, expiresAt } = action.payload
      const current = state.localOverridesByRelation[relationNumber] ?? {}

      state.localOverridesByRelation[relationNumber] = {
        ...current,
        [key]: value,
        expiresAt: expiresAt ?? dateUtils.getEndOfDay(new Date()).getTime()
      }
    },
    setLocalRelationToggles: (
      state,
      action: PayloadAction<{
        relationNumber: string
        toggles: Partial<Pick<RelationOverrides, RelationToggleKey>>
        expiresAt?: number
      }>
    ) => {
      const { relationNumber, toggles, expiresAt } = action.payload
      const current = state.localOverridesByRelation[relationNumber] ?? {}

      state.localOverridesByRelation[relationNumber] = {
        ...current,
        ...toggles,
        expiresAt: expiresAt ?? dateUtils.getEndOfDay(new Date()).getTime()
      }
    },
    clearLocalRelationOverrides: (state, action: PayloadAction<{ relationNumber: string }>) => {
      delete state.localOverridesByRelation[action.payload.relationNumber]
    },
    pruneExpiredOverrides: (state) => {
      const now = Date.now()

      for (const [rel, overrides] of Object.entries(state.localOverridesByRelation)) {
        if (overrides.expiresAt && overrides.expiresAt <= now) {
          delete state.localOverridesByRelation[rel]
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
      .addCase(refreshSession, () => initialState)
  }
})

export const {
  setResourceAuthorization,
  setResourceAuthorizationIsLoading,
  setResourceAuthorizations,
  setLocalRelationToggle,
  setLocalRelationToggles,
  clearLocalRelationOverrides,
  pruneExpiredOverrides,
  setManagedResourceAuthorizations
} = resourceAuthorizationSlice.actions

export default resourceAuthorizationSlice.reducer
